import React, { useState, useEffect } from "react";
import BackButton from "./BackButton";
import axios from "axios";
import { BASE_URL } from "../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpLong,
  faArrowDownLong,
  faSpinner,
  faTimes,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
} from "@fortawesome/free-solid-svg-icons";
import "./LoaTransactionHistoryModal.css";
import { useAuth } from "../contexts/AuthContext";
import { formatToPHP } from "../utils/currencyUtils";

const LoaTransactionHistoryModal = ({ isOpen, onClose }) => {
  const { encryptedData } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "trans_date",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchTransactionHistory();
    }
  }, [isOpen, currentPage, itemsPerPage, sortConfig, filter]);

  const fetchTransactionHistory = async () => {
    setLoading(true);
    try {
      const payload = {
        data: encryptedData,
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
        filter,
      };
      const formData = new FormData();
      Object.keys(payload).forEach((key) => formData.append(key, payload[key]));

      const response = await axios.post(
        `${BASE_URL}/x1bf/x1wh/member/loa-transaction-history`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );

      setData(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (error) {
      console.error("Error fetching transaction history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const maxPagesToShow = 5;
    const pages = [];
    const halfRange = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage <= halfRange) {
        endPage = Math.min(maxPagesToShow, totalPages);
      } else if (currentPage + halfRange >= totalPages) {
        startPage = Math.max(1, totalPages - maxPagesToShow + 1);
      }
    }

    pages.push(
      <button
        key="first"
        onClick={() => setCurrentPage(1)}
        disabled={currentPage === 1}
        className={`pagination-btn ${currentPage === 1 ? "disabled" : ""}`}
      >
        <FontAwesomeIcon icon={faAngleDoubleLeft} />
        <span className="pagination-text">First</span>
      </button>
    );

    pages.push(
      <button
        key="prev"
        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        disabled={currentPage === 1}
        className={`pagination-btn ${currentPage === 1 ? "disabled" : ""}`}
      >
        <FontAwesomeIcon icon={faAngleLeft} />
        <span className="pagination-text">Previous</span>
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`pagination-btn ${currentPage === i ? "active" : ""}`}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
        disabled={currentPage === totalPages}
        className={`pagination-btn ${currentPage === totalPages ? "disabled" : ""}`}
      >
        <span className="pagination-text">Next</span>
        <FontAwesomeIcon icon={faAngleRight} />
      </button>
    );

    pages.push(
      <button
        key="last"
        onClick={() => setCurrentPage(totalPages)}
        disabled={currentPage === totalPages}
        className={`pagination-btn ${currentPage === totalPages ? "disabled" : ""}`}
      >
        <span className="pagination-text">Last</span>
        <FontAwesomeIcon icon={faAngleDoubleRight} />
      </button>
    );

    return pages;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-backrow"><BackButton onClick={onClose} /></div>
          <h1 className="modal-title">Transaction History</h1>
          <button onClick={onClose} className="modal-close-btn">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-body">
          <input
            type="text"
            placeholder="Filter by provider name"
            value={filter}
            onChange={handleFilterChange}
            className="filter-input"
          />
          {loading ? (
            <div className="loading-spinner">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            </div>
          ) : (
            <div className="table-container">
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("provider_name")}>
                      Provider Name
                      <FontAwesomeIcon
                        icon={
                          sortConfig.key === "provider_name" &&
                          sortConfig.direction === "asc"
                            ? faArrowUpLong
                            : faArrowDownLong
                        }
                        className="sort-icon"
                      />
                    </th>
                    <th onClick={() => handleSort("trans_date")}>
                      Transaction Date
                      <FontAwesomeIcon
                        icon={
                          sortConfig.key === "trans_date" &&
                          sortConfig.direction === "asc"
                            ? faArrowUpLong
                            : faArrowDownLong
                        }
                        className="sort-icon"
                      />
                    </th>
                    <th>LOA Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length > 0 ? (
                    data.map((row, index) => (
                      <tr key={index}>
                        <td>{row.provider_name || "N/A"}</td>
                        <td>
                          {row.trans_date
                            ? new Date(row.trans_date).toLocaleString()
                            : "N/A"}
                        </td>
                        <td>{formatToPHP(row.loa_amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="no-data">
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="pagination">{renderPagination()}</div>
        </div>
      </div>
    </div>
  );
};

export default LoaTransactionHistoryModal;